import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Box, CssBaseline, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';

import Header from './Components/Header';
import MainContent from './Components/MainContent';
import CreateRule from './Components/CreateRule';
import CountrySelector from './Components/CountrySelector';
import ReviewRule from './Components/ReviewRule';
import LoginPage from './Components/LoginPage';
import { LoginRequired } from './auth';
import { setNavigateFunction } from './auth/events';
import { checkCurrentUserAdmin } from './graphql';
import { handleOAuthCallback, logout as directLogout, startTokenRefreshTimer, stopTokenRefreshTimer, setSessionExpiredCallback, isAuthenticated, setSessionExpiredActive } from './auth/directOAuthHandler';
import { setOnAuthError } from './api/apolloClient';

// Dashboard Layout Component
const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const [currentView, setCurrentView] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Check for navigation state to set initial view
  useEffect(() => {
    const state = location.state as { view?: string } | null;
    if (state?.view) {
      setCurrentView(state.view);
    }
  }, [location.state]);

  // Check user role on component mount
  useEffect(() => {
    const checkUserRole = async () => {
      // Set a timeout to prevent infinite loading
      const timeout = setTimeout(() => {
        console.warn('User role check timed out after 10 seconds');
        setIsAdmin(false);
        setLoading(false);
      }, 10000); // 10 second timeout

      try {
        const result = await checkCurrentUserAdmin();
        clearTimeout(timeout);
        setIsAdmin(result.isAdmin);
        setLoading(false);

      } catch (error) {
        clearTimeout(timeout);
        console.error('Failed to check user role:', error);
        // Default to non-admin if check fails
        setIsAdmin(false);
        setLoading(false);
      }
    };

    checkUserRole();
  }, []);

  const handleViewChange = (view: string) => {
    setCurrentView(view);
  };

  // Don't show loading spinner - render layout immediately with skeleton/placeholder
  // This prevents the jerky "flash" of multiple loading screens

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Full-width Header spanning entire dashboard */}
      <Header onLogout={directLogout} onViewChange={handleViewChange} isAdmin={isAdmin} />
      
      {/* Main content area */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'auto'
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <MainContent currentView={currentView} onViewChange={handleViewChange} isAdmin={isAdmin} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Main App Component with Routing and Auth
const App: React.FC = () => {
  const navigate = useNavigate();
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [callbackProcessed, setCallbackProcessed] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  // Set up session expired callback and start refresh timer
  useEffect(() => {
    const handleExpired = () => {
      setSessionExpiredActive(true);
      setShowSessionExpired(true);
    };
    setSessionExpiredCallback(handleExpired);
    setOnAuthError(handleExpired);

    if (isAuthenticated()) {
      startTokenRefreshTimer();
    }

    return () => {
      stopTokenRefreshTimer();
    };
  }, []);

  const handleSessionExpiredLogin = () => {
    setShowSessionExpired(false);
    setSessionExpiredActive(false);
    directLogout();
  };

  // Handle OAuth callback when Okta redirects back - ONLY ONCE
  useEffect(() => {
    if (callbackProcessed) {

      return;
    }

    const handleCallback = async () => {

      // Check if this is an OAuth callback URL
      const hasOAuthCode = window.location.search.includes('code=');
      
      if (!hasOAuthCode) {

        setCallbackProcessed(true);
        setAuthCheckComplete(true);
        return;
      }
      
      const callbackHandled = await handleOAuthCallback();
      
      setCallbackProcessed(true);
      setAuthCheckComplete(true);
      
      if (callbackHandled) {

        startTokenRefreshTimer();
        navigate('/dashboard', { replace: true });
      } else {

      }
    };
    
    handleCallback();
  }, [navigate, callbackProcessed]);

  // Simple authentication check - no complex logic
  if (!authCheckComplete) {
    // Still processing OAuth callback

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // After auth check complete, LoginRequired will handle the rest

  return (
    <>
      <CssBaseline />
      <Dialog
        open={showSessionExpired}
        disableEscapeKeyDown
        sx={{ '& .MuiDialog-paper': { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Session Expired</DialogTitle>
        <DialogContent>
          <Typography>
            Your session has expired. Please log in again to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleSessionExpiredLogin}
            sx={{ backgroundColor: '#2196F3', textTransform: 'none', fontWeight: 600 }}
          >
            Log In Again
          </Button>
        </DialogActions>
      </Dialog>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        
        <Route
          path="/"
          element={
            <LoginRequired>
              <DashboardLayout />
            </LoginRequired>
          }
        />
        <Route
          path="/dashboard"
          element={
            <LoginRequired>
              <DashboardLayout />
            </LoginRequired>
          }
        />
        <Route
          path="/create-rule"
          element={
            <LoginRequired>
              <CreateRule />
            </LoginRequired>
          }
        />
        <Route
          path="/country-selector"
          element={
            <LoginRequired>
              <CountrySelector />
            </LoginRequired>
          }
        />
        <Route
          path="/review-rule"
          element={
            <LoginRequired>
              <ReviewRule />
            </LoginRequired>
          }
        />
      </Routes>
    </>
  );
};

// Root component with Router
const AppWithRouter: React.FC = () => {
  return (
    <Router>
      <App />
    </Router>
  );
};

export default AppWithRouter;
